import pclnExperimentation from '@pcln/experimentation'
import type { AutocompleteSlotProps, PackageFormTripType, PackagesFormState } from '@pcln/horizon-search-form'
import { isHotelFlight, isHotelFlightCar, isHotelPlusCar, PackageSearchForm } from '@pcln/horizon-search-form'
import { useCallback, useEffect, useRef, useState } from 'react'

import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import type { TypeAheadConfigEndpoint } from '@/components/TypeAhead/types'
import useSeti from '@/hooks/useSeti'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'

import {
  fireCustomizeCarCheckboxClick,
  fireCustomizeHotelCheckboxClick,
  trackCustomPackageSearchPromotionClick,
  trackCustomPackageSearchPromotionView,
} from '../ga4'
import type { PackagesFormStateType } from '../types'
import usePackagesFormInitialValues from './usePackagesFormInitialValues'

const DEFAULT_PICKUP_TIME = '12:00'
const DEFAULT_DROPOFF_TIME = '12:00'
const ADVANCED_SEARCH_DEFAULT_COPY = 'Advanced Search (One-Way Flight, Multi-Hotel)'
const ADVANCED_SEARCH_VARIANT_COPY = 'Custom Package Search (One-Way Flight, Multi-Hotel)'

const renderAutocomplete = (props: AutocompleteSlotProps) => (
  <AutocompleteWrapper
    ref={props.inputRef}
    searchKey={props.searchKey}
    label={props.label}
    typeAheadEndpoint={props.typeAheadEndpoint as TypeAheadConfigEndpoint}
    errorMessage={props.errorMessage}
    initialSelectedItem={props.initialSelectedItem}
    onItemSelect={(item) =>
      props.onItemSelect(item as import('@pcln/horizon-search-form').LocationSearchType | null)
    }
  />
)

export default function HorizonPackagesForm({
  onSubmit,
  pageName,
}: Readonly<{
  onSubmit: (pageName: 'homepage' | 'landing', data: PackagesFormStateType) => void
  pageName: 'homepage' | 'landing'
}>) {
  const { initialValues, isMobile, cguid, localeCode } = usePackagesFormInitialValues()
  const flyTypeaheadSeti = useSeti({ experimentCode: 'PKG_AC_FLY_TYPEAHEAD', shouldFireImpression: false })
  const useFlightTypeaheadForFlyDrive = flyTypeaheadSeti === 'VARIANT'

  const isCustomPackageSearchCopySeti =
    useSeti({
      experimentCode: 'PKG_ADVANCED_SEARCH_COPY_UPDATE',
      shouldFireImpression: false,
    }) === 'VARIANT'
  const hasFiredCopyUpdateImpressionRef = useRef(false)
  const fireCopyUpdateImpression = () => {
    if (!hasFiredCopyUpdateImpressionRef.current) {
      hasFiredCopyUpdateImpressionRef.current = true
      pclnExperimentation.fireImpression('PKG_ADVANCED_SEARCH_COPY_UPDATE')
      if (isCustomPackageSearchCopySeti) {
        trackCustomPackageSearchPromotionView(pageName)
      }
    }
  }
  const isCustomizeCarCopyVariant =
    useSeti({ experimentCode: 'PKG_HC_SEARCH_CUSTOMIZE_CAR_COPY', shouldFireImpression: false }) === 'VARIANT'
  const isCustomizeHotelCopyVariant =
    useSeti({ experimentCode: 'PKG_SEARCH_CUSTOMIZE_HOTEL_COPY', shouldFireImpression: false }) === 'VARIANT'

  const [currentTripType, setCurrentTripType] = useState<PackageFormTripType>(initialValues.tripType)
  const hasFiredCustomizeCarImpression = useRef(false)
  useEffect(() => {
    if (isHotelPlusCar(currentTripType) && !hasFiredCustomizeCarImpression.current) {
      pclnExperimentation.fireImpression('PKG_HC_SEARCH_CUSTOMIZE_CAR_COPY')
      hasFiredCustomizeCarImpression.current = true
    }
  }, [currentTripType])
  const hasFiredCustomizeHotelImpression = useRef(false)
  useEffect(() => {
    if (
      (isHotelFlight(currentTripType) || isHotelFlightCar(currentTripType)) &&
      !hasFiredCustomizeHotelImpression.current
    ) {
      pclnExperimentation.fireImpression('PKG_SEARCH_CUSTOMIZE_HOTEL_COPY')
      hasFiredCustomizeHotelImpression.current = true
    }
  }, [currentTripType])

  const handleSubmit = useCallback(
    (data: PackagesFormState) => {
      if (data.tripType === 'FLY:DRIVE') {
        pclnExperimentation.fireImpression('PKG_AC_FLY_TYPEAHEAD')
      }
      onSubmit(pageName, data as PackagesFormStateType)
      logSearchFormAnalytics({ cguid, pageName, productType: 'vacations', isMobile })
    },
    [onSubmit, pageName, cguid, isMobile],
  )

  return (
    <PackageSearchForm
      initialValues={initialValues as unknown as Partial<PackagesFormState>}
      isMobile={isMobile}
      autoAdvance={!isMobile}
      localeCode={localeCode}
      pageName={pageName}
      defaultPickupTime={DEFAULT_PICKUP_TIME}
      defaultDropoffTime={DEFAULT_DROPOFF_TIME}
      useFlightTypeaheadForFlyDrive={useFlightTypeaheadForFlyDrive}
      advancedSearchCopy={
        isCustomPackageSearchCopySeti ? ADVANCED_SEARCH_VARIANT_COPY : ADVANCED_SEARCH_DEFAULT_COPY
      }
      onAdvancedSearchClick={() => {
        if (isCustomPackageSearchCopySeti) {
          trackCustomPackageSearchPromotionClick(pageName)
        }
      }}
      onAdvancedSearchVisible={fireCopyUpdateImpression}
      hotelCarCheckboxLabel={
        isCustomizeCarCopyVariant ? 'Customize Car Search' : 'I want to pick up my car somewhere else'
      }
      onHotelCarCheckboxToggle={() => fireCustomizeCarCheckboxClick(pageName)}
      partialStayCheckboxLabel={isCustomizeHotelCopyVariant ? 'Customize Hotel Search' : undefined}
      onPartialStayCheckboxToggle={() =>
        fireCustomizeHotelCheckboxClick(pageName, isHotelFlightCar(currentTripType) ? 'ahc' : 'ha')
      }
      onTripTypeChange={setCurrentTripType}
      onSubmit={handleSubmit}
      renderAutocomplete={renderAutocomplete}
    />
  )
}
